import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { IconRenderer } from "../lib/icons";
import Header from "../components/Header";
import Modal from "../components/Modal";
import IconPicker from "../components/IconPicker";

export default function ChoiceEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [options, setOptions] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [pickerFor, setPickerFor] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: profiles } = await api.get("/profiles");
        const pid = localStorage.getItem("rb_profile");
        setProfileId(profiles.find((p) => p.id === pid)?.id || profiles[0]?.id || "");
        if (isEdit) {
          const { data } = await api.get(`/choices/${id}`);
          setName(data.name);
          setOptions(data.options);
          setProfileId(data.profile_id);
        }
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
  }, [id, isEdit]);

  const updateOption = (i, patch) => setOptions((o) => o.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeOption = (i) => setOptions((o) => o.filter((_, idx) => idx !== i));
  const addOption = () => setOptions((o) => [...o, { id: `tmp-${Date.now()}-${o.length}`, label: "", icon: "Star" }]);

  const save = async () => {
    const clean = options.filter((o) => o.label.trim());
    if (!name.trim()) return toast.error("Give the choice board a name");
    if (clean.length < 2) return toast.error("Add at least 2 options");
    setSaving(true);
    try {
      const payload = { profile_id: profileId, name: name.trim(), options: clean };
      if (isEdit) await api.put(`/choices/${id}`, payload);
      else await api.post("/choices", payload);
      toast.success(isEdit ? "Choice board saved" : "Choice board created");
      navigate("/app");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="choice-editor" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8 pb-24">
        <button data-testid="back-button" onClick={() => navigate("/app")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>

        <h1 className="mt-6 text-3xl font-extrabold tracking-tight">{isEdit ? "Edit choice board" : "New choice board"}</h1>
        <p className="mt-1 text-lg text-muted-foreground">A simple question with 2–8 picture options, e.g. "What snack would you like?"</p>

        <div className="mt-6 bg-white rounded-3xl border-2 border-foreground/10 p-6">
          <label htmlFor="choice-name" className="block text-base font-bold mb-1">Question</label>
          <input
            id="choice-name"
            data-testid="choice-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. What snack would you like?"
            className="w-full min-h-[56px] rounded-2xl border-2 border-foreground/25 px-4 text-lg focus:outline-none focus:border-primary"
          />
        </div>

        <div className="mt-6 space-y-4">
          <h2 className="text-2xl font-extrabold">Options ({options.length})</h2>
          {options.map((opt, i) => (
            <div key={opt.id} data-testid={`choice-option-row-${i}`} className="bg-white rounded-3xl border-2 border-foreground/10 p-4 flex items-center gap-3">
              <button
                data-testid={`choice-icon-button-${i}`}
                onClick={() => setPickerFor(i)}
                aria-label={`Change icon for option ${i + 1}`}
                className="w-16 h-16 rounded-2xl border-2 border-foreground/15 grid place-items-center shrink-0 hover:bg-secondary transition-colors duration-200"
              >
                <IconRenderer name={opt.icon} size={32} className="text-[#ED8936]" />
              </button>
              <input
                data-testid={`choice-label-input-${i}`}
                value={opt.label}
                onChange={(e) => updateOption(i, { label: e.target.value })}
                placeholder={`Option ${i + 1}, e.g. Apple`}
                className="flex-1 min-w-0 min-h-[56px] rounded-2xl border-2 border-foreground/25 px-4 text-lg focus:outline-none focus:border-primary"
              />
              <button data-testid={`choice-option-delete-${i}`} onClick={() => removeOption(i)} aria-label="Delete option" className="min-w-[44px] min-h-[44px] rounded-full grid place-items-center text-[#C53030] hover:bg-[#F56565]/10 transition-colors duration-200">
                <Trash2 size={20} strokeWidth={2.5} />
              </button>
            </div>
          ))}
          <button
            data-testid="add-option-button"
            onClick={addOption}
            disabled={options.length >= 8}
            className="w-full min-h-[64px] rounded-3xl border-2 border-dashed border-foreground/30 bg-white font-bold text-lg flex items-center justify-center gap-2 hover:bg-secondary transition-colors duration-200 disabled:opacity-50"
          >
            <Plus size={24} strokeWidth={3} /> Add an option
          </button>
        </div>

        <button
          data-testid="save-choice-button"
          onClick={save}
          disabled={saving}
          className="mt-8 w-full min-h-[64px] rounded-full bg-primary text-white text-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Check size={26} strokeWidth={3} /> {saving ? "Saving…" : "Save choice board"}
        </button>
      </main>

      {pickerFor !== null && (
        <Modal title="Pick an icon" onClose={() => setPickerFor(null)} testId="icon-picker-modal">
          <IconPicker value={options[pickerFor]?.icon} onChange={(name) => { updateOption(pickerFor, { icon: name }); setPickerFor(null); }} />
        </Modal>
      )}
    </div>
  );
}
